import React, { useState } from 'react'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { FaChevronDown, FaChevronUp } from 'react-icons/fa6'
import PlanetImage1 from '@/images/Planet-1.png'
import PlanetImage2 from '@/images/Planet-2.png'
import PlanetImage3 from '@/images/Planet-3.png'
import { MdDone } from 'react-icons/md' 

const AccordionItem = ({ item, isOpen, onToggle }) => {
    return (
        <div className="rounded-[12px] p-[14px] transition-all duration-300 bg-white">
            <button
                onClick={onToggle}
                className="flex w-full items-center justify-between text-left"
            >
                <div className="flex items-center gap-3 flex-1">
                    <span
                        className="min-w-[25px] min-h-[25px] rounded-full flex items-center justify-center relative"
                    >
                        <span
                            className="absolute inset-0 rounded-full bg-main-pink/40"
                        ></span>
                        <MdDone className="w-[18px] h-[18px] relative z-10 text-main-pink" />
                    </span>
                    <span className="text-[14px] lg:text-[16px] font-[500] text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884]">
                        {item.title}
                    </span>
                </div>
                <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                    {isOpen ? (
                        <FaChevronUp className="text-[#1A3884] text-[16px]" />
                    ) : (
                        <FaChevronDown className="text-[#1A3884] text-[16px]" />
                    )}
                </div>
            </button>
            <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100 mt-[8px]' : 'max-h-0 opacity-0'
                    }`}
            >
                {item.content && (
                    <div className="flex flex-col pl-[10px]">
                        {Array.isArray(item.content) ? (
                            item.content.map((contentItem, index) => (
                                <div key={index} className="flex items-start gap-[10px]">
                                    <span className="text-[16px] font-[400] text-[#222222] leading-relaxed">
                                        •
                                    </span>
                                    <span className="text-[16px] font-[400] text-[#222222] leading-relaxed">
                                        {contentItem}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="flex items-start gap-[10px]">
                                <span className="text-[16px] font-[400] text-[#222222] leading-relaxed">
                                    •
                                </span>
                                <span className="text-[16px] font-[400] text-[#222222] leading-relaxed">
                                    {item.content}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

const FeatureSection = ({ section, planetImage }) => {
    const { t } = useTranslation()
    const [openItemId, setOpenItemId] = useState(() => section.defaultOpen || null)

    const toggleItem = (id) => {
        setOpenItemId((prev) => (prev === id ? null : id))
    }

    return (
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-[40px] items-start bg-[#F5F7F9] rounded-[40px] md:rounded-[64px] xl:px-[60px] xl:py-[100px] md:px-[30px] md:py-[50px] px-[16px] py-[40px]">
            <div className="w-full lg:w-[50%] flex flex-col gap-[20px]">
                <h2 className="text-[32px] font-[600] leading-[120%] text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884]">
                    <span dangerouslySetInnerHTML={{ __html: section.title }} />
                </h2>

                <p
                    className="text-[16px] font-[400] leading-[160%]"
                    dangerouslySetInnerHTML={{ __html: section.description }}
                />

                <div className="flex flex-col gap-[12px]">
                    {section.items.map((item) => (
                        <AccordionItem
                            key={item.id}
                            item={item}
                            isOpen={openItemId === item.id}
                            onToggle={() => toggleItem(item.id)}
                        />
                    ))}
                </div>
            </div>
            <div className="w-full lg:w-[50%] flex-shrink-0 flex items-center justify-center">
                <div className="relative w-full max-w-[560px] aspect-square sm:aspect-[4/3] lg:aspect-[4/3] xl:aspect-[1/1] rounded-[24px] lg:rounded-[32px] overflow-hidden">
                    <Image
                        src={planetImage}
                        alt="World Planet"
                        className="object-contain"
                        fill
                        loading="lazy"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 560px"
                    />
                </div>
            </div>
        </div>
    )
}

const ProgramFeature = () => {
    const { t } = useTranslation()

    const sections = [
        {
            id: 1,
            title: t('programs-page.program-feature-section-1-title'),
            description: t('programs-page.program-feature-section-1-description'),
            titleColor: 'blue',
            items: [
                {
                    id: 1,
                    title: t('programs-page.program-feature-section-1-item-1-title'),
                    content: t('programs-page.program-feature-section-1-item-1-content'),
                },
                {
                    id: 2,
                    title: t('programs-page.program-feature-section-1-item-2-title'),
                    content: t('programs-page.program-feature-section-1-item-2-content'),
                },
                {
                    id: 3,
                    title: t('programs-page.program-feature-section-1-item-3-title'),
                    content: t('programs-page.program-feature-section-1-item-3-content'),
                },
            ],
        },
        {
            id: 2,
            title: t('programs-page.program-feature-section-2-title'),
            description: t('programs-page.program-feature-section-2-description'),
            titleColor: 'red',
            items: [
                {
                    id: 1,
                    title: t('programs-page.program-feature-section-2-item-1-title'),
                    content: t('programs-page.program-feature-section-2-item-1-content'),
                },
                {
                    id: 2,
                    title: t('programs-page.program-feature-section-2-item-2-title'),
                    content: t('programs-page.program-feature-section-2-item-2-content'),
                },
            ],
        },
        {
            id: 3,
            title: t('programs-page.program-feature-section-3-title'),
            description: t('programs-page.program-feature-section-3-description'),
            titleColor: 'red',
            items: [
                {
                    id: 1,
                    title: t('programs-page.program-feature-section-3-item-1-title'),
                    content: t('programs-page.program-feature-section-3-item-1-content'),
                },
                {
                    id: 2,
                    title: t('programs-page.program-feature-section-3-item-2-title'),
                    content: t('programs-page.program-feature-section-3-item-2-content'),
                },
                {
                    id: 3,
                    title: t('programs-page.program-feature-section-3-item-3-title'),
                    content: t('programs-page.program-feature-section-3-item-3-content'),
                },
            ],
        },
    ]

    const planetImages = [PlanetImage3, PlanetImage2, PlanetImage1]

    return (
        <section className="py-[60px] md:py-[100px] bg-white max-w-[90%] mx-auto">
            <div className="flex flex-col gap-[60px] md:gap-[40px]">
                {sections.map((section) => (
                    <FeatureSection 
                        key={section.id} 
                        section={section} 
                        planetImage={planetImages[section.id - 1]}
                    />
                ))}
            </div>
        </section>
    )
}

export default ProgramFeature
